import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Tabs, Alert, Spinner, Icon, EmptyState } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { vendorApiClient, type LocationsListResponse, type VendorLocation } from '../../lib/vendorApiClient';
import { hoursTodayLabel, normalizeWeek } from '../../lib/hours';
import LocationStatusChip from '../../components/LocationStatusChip';

/**
 * Locations — L0 (empty, new vendor) and L1 (list). Figma `fVTA2OnSEz4UOdTeoXeByH`
 * 11:81 / 7:2. Every store the vendor sells from; prices, sales and circulars
 * are set per location (§15n). A verifying location is hidden from shoppers
 * until a human confirms it (§15gg V8); the amber banner counts them.
 */

const HEADING = '#303A42';
const MUTED = '#5A7063';
const BORDER = '#E5E5E5';
const ORANGE_INK = '#A85200';
const GREEN_INK = '#1F7026';

type Filter = 'all' | 'live' | 'verifying' | 'needs_changes';

export default function LocationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const canManage = has('location:manage');
  const [data, setData] = useState<LocationsListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');

  const load = useCallback((vendorId: string) => {
    setError(null);
    vendorApiClient
      .listLocations(vendorId)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (activeVendorId) load(activeVendorId);
  }, [activeVendorId, load]);

  const items = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.items.filter((l) => {
      if (filter !== 'all' && l.status !== filter) return false;
      if (!needle) return true;
      return [l.name, l.address.city, l.address.postalCode, l.address.formatted].some((v) => v.toLowerCase().includes(needle));
    });
  }, [data, filter, q]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  // ── L0: a new vendor with no locations yet ─────────────────────────────
  if (data.counts.all === 0) {
    return (
      <Box sx={{ maxWidth: 760 }}>
        <PageTitle title={t('locations.title')} />
        <EmptyState
          icon="location"
          title={t('locations.empty.title')}
          body={t('locations.empty.body')}
          primaryAction={canManage ? { label: t('locations.addLocation'), onClick: () => navigate('/locations/new'), icon: 'add' } : undefined}
          secondaryAction={canManage ? { label: t('locations.importLocations'), onClick: () => navigate('/locations/import') } : undefined}
          backgroundColor="#fff"
          border={`1px solid ${BORDER}`}
          borderRadius={10}
          iconColor={ORANGE_INK}
        />
        <Typography sx={{ mt: 2, fontSize: 13, color: MUTED }}>{t('locations.empty.importHint')}</Typography>
      </Box>
    );
  }

  const hq = data.headquarters;
  const hqAddress = formatHq(hq.address);

  // ── L1: the list ───────────────────────────────────────────────────────
  return (
    <Box data-testid="locations-list">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <PageTitle title={t('locations.title')} subtitle={t('locations.subtitle')} />
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1.5, pt: 1 }}>
            <Button ariaLabel={t('locations.archives.title')} buttonText={t('locations.archives.title')} variant="text" onClick={() => navigate('/locations/archived')} />
            <Button ariaLabel={t('locations.importLocations')} buttonText={t('locations.importLocations')} variant="outlined" onClick={() => navigate('/locations/import')} />
            <Button ariaLabel={t('locations.addLocation')} buttonText={t('locations.addLocation')} variant="contained" color="primary" onClick={() => navigate('/locations/new')} />
          </Box>
        )}
      </Box>

      {data.counts.verifying > 0 && (
        <Alert severity="warning" title={t('locations.verifyingBanner.title', { count: data.counts.verifying })} sx={{ mt: 2 }}>
          {t('locations.verifyingBanner.body')}
        </Alert>
      )}

      {/* Headquarters — Vendor.address (§15d), never shown to shoppers */}
      <Box sx={{ mt: 2, p: 2.5, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: HEADING }}>{t('locations.hq.title')}</Typography>
            <Typography sx={{ fontSize: 13, color: MUTED }}>{t('locations.hq.hint')}</Typography>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: '#343434' }}>
            {hq.businessName}
            {hqAddress ? ` · ${hqAddress}` : ''}
          </Typography>
        </Box>
        <Button ariaLabel={t('common.edit')} buttonText={t('common.edit')} variant="text" onClick={() => navigate('/business/profile')} />
      </Box>

      {/* Search + status tabs */}
      <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 320px', maxWidth: 360 }}>
          <TextField ariaLabel={t('locations.searchAria')} placeholder={t('locations.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} size="small" fullWidth />
        </Box>
        <Tabs
          ariaLabel={t('locations.filterAria')}
          value={filter}
          onChange={(_e, v) => setFilter(v as Filter)}
          color={ORANGE_INK}
          tabs={[
            { value: 'all', label: `${t('locations.status.all')}  ${data.counts.all}` },
            { value: 'live', label: `${t('locations.status.live')}  ${data.counts.live}` },
            { value: 'verifying', label: `${t('locations.status.verifying')}  ${data.counts.verifying}` },
            { value: 'needs_changes', label: `${t('locations.status.needsChanges')}  ${data.counts.needs_changes}` },
          ]}
        />
      </Box>

      {/* Table — DSL primitives (the DSL has no table component) */}
      <Box role="table" aria-label={t('locations.tableAria')} sx={{ mt: 2, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '2.4fr 1.4fr 1.4fr 1fr 1fr 40px', gap: 2, px: 2.5, py: 1.5, bgcolor: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
          {['location', 'hoursToday', 'services', 'productsPriced', 'status'].map((k) => (
            <Typography key={k} role="columnheader" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED }}>
              {t(`locations.columns.${k}`).toUpperCase()}
            </Typography>
          ))}
          <span />
        </Box>
        {items.length === 0 ? (
          <Typography sx={{ px: 2.5, py: 3, color: MUTED, fontSize: 14 }}>{t('locations.noMatch')}</Typography>
        ) : (
          items.map((l) => <LocationRow key={l.storeId} location={l} />)
        )}
      </Box>
    </Box>
  );
}

function LocationRow({ location: l }: { location: VendorLocation }) {
  const { t } = useTranslation();
  const today = hoursTodayLabel(normalizeWeek(l.hours));
  const services = [
    l.pickupSupported ? t('locations.services.pickup') : null,
    l.deliveryCoverageDerived ? t('locations.services.deliveryCoverageShort') : null,
    l.snapAccepted ? t('locations.services.snap') : null,
  ].filter(Boolean);
  const sub =
    l.status === 'needs_changes' && l.statusNote
      ? { text: l.statusNote, color: '#C62828' }
      : l.source === 'import'
        ? { text: t('locations.addedByImport', { date: fmtDate(l.submittedAt) }), color: ORANGE_INK }
        : null;

  return (
    <Box
      component={Link}
      to={`/locations/${l.storeId}`}
      role="row"
      aria-label={l.name}
      data-testid={`location-row-${l.storeId}`}
      data-status={l.status}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 40px', md: '2.4fr 1.4fr 1.4fr 1fr 1fr 40px' },
        gap: 2,
        alignItems: 'center',
        px: 2.5,
        py: 2,
        borderBottom: `1px solid ${BORDER}`,
        textDecoration: 'none',
        color: 'inherit',
        '&:last-of-type': { borderBottom: 'none' },
        '&:hover': { bgcolor: '#FAFAFA' },
      }}
    >
      <Box role="cell" sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: HEADING }}>{l.name}</Typography>
        <Typography sx={{ fontSize: 13, color: MUTED }}>{l.address.formatted}</Typography>
        {sub && <Typography sx={{ fontSize: 12, color: sub.color, mt: 0.25 }}>{sub.text}</Typography>}
      </Box>
      <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 14, color: '#343434' }}>
        {l.status === 'live' ? (today.open ? `${t('locations.openToday')} · ${today.text}` : t('locations.closedToday')) : '—'}
      </Typography>
      <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 14, color: '#343434' }}>{services.join(' · ') || '—'}</Typography>
      <Typography role="cell" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 14, fontWeight: 700, color: l.status === 'verifying' && l.productsPriced ? ORANGE_INK : HEADING }}>
        {l.status === 'verifying' && l.productsPriced ? t('locations.waiting', { count: l.productsPriced }) : (l.productsPriced ?? 0)}
      </Typography>
      <Box role="cell" sx={{ display: { xs: 'none', md: 'block' } }}>
        <LocationStatusChip status={l.status} />
      </Box>
      <Icon name="chevronRight" size={20} color={GREEN_INK} />
    </Box>
  );
}

function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box>
      <Typography component="h1" sx={{ fontSize: 40, lineHeight: 1.2, fontWeight: 700, color: HEADING }}>
        {title}
      </Typography>
      {subtitle && <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>{subtitle}</Typography>}
    </Box>
  );
}

function formatHq(address: unknown): string {
  if (!address || typeof address !== 'object') return '';
  const a = address as Record<string, unknown>;
  const parts = [a.line1, a.line2, a.city, [a.state, a.postalCode].filter(Boolean).join(' ')].filter((v) => typeof v === 'string' && v.trim());
  return parts.join(', ');
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
